import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import { localizedPath } from "src/utils/languagePaths"
import { selectPaymentClientId } from "src/store/common"
import { captureOrder, createOrder, selectCurrency } from "src/store/economy"
import { loadPayPalSdk } from "src/utils/paypal"

import "./PayPalCheckout.css"

/* PayPal's own buttons, for one credit pack.

   Three callbacks, and what each is allowed to know:

   - `createOrder` asks *our* backend to open the order and hands PayPal the id it answers with. The
     price is never sent from here: the server reads it from its catalogue and writes down what
     the payment is worth before the player is sent anywhere.
   - `onApprove` tells our backend to capture. It does NOT capture in the browser — `actions.order`
     could, and that would make this component the source of truth for whether money arrived.
     All it says is "this order was approved"; PayPal tells the server whether it was paid.
   - `onError` / `onCancel` report and leave the balance alone.

   The buttons are re-created whenever the product changes, because `createOrder` is captured in
   the closure PayPal holds — a re-render alone would leave the previous product's payment being
   opened by a button labelled with the new one's price. */
const PayPalCheckout = ({ product, onPaid, onFailed }) => {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()
  const clientId = useSelector(selectPaymentClientId)
  const currency = useSelector(selectCurrency)
  const container = useRef(null)
  const [state, setState] = useState("loading")

  // Express consent to immediate delivery, given before paying (EU withdrawal waiver). Never
  // remembered: a new pack, or a new checkout, starts unticked. The PayPal buttons read it through
  // a ref, because `createOrder` is captured once by the SDK.
  const [waived, setWaived] = useState(false)
  const waivedRef = useRef(false)
  waivedRef.current = waived
  const language = (i18n.language || "en").slice(0, 2)
  const buttonActions = useRef(null)
  useEffect(() => setWaived(false), [product])
  useEffect(() => {
    if (waived) buttonActions.current?.enable()
    else buttonActions.current?.disable()
  }, [waived])

  // Held in a ref so a parent re-rendering with new closures does not re-run the effect and make
  // PayPal paint a second set of buttons into the same div — the same reason `GoogleSignInButton`
  // does it.
  const handlers = useRef({ onPaid, onFailed })
  handlers.current = { onPaid, onFailed }

  useEffect(() => {
    if (!clientId || !product) return undefined

    let cancelled = false
    let buttons = null

    loadPayPalSdk(clientId, currency)
      .then((paypal) => {
        if (cancelled || !container.current) return
        container.current.innerHTML = ""

        buttons = paypal.Buttons({
          style: { layout: "vertical", shape: "rect", label: "paypal", height: 40 },

          // Disabled until the box is ticked. `onClick` is the guard that cannot be skipped: it
          // refuses the click itself, whatever state the iframe is drawn in.
          onInit: (data, actions) => {
            buttonActions.current = actions
            if (!waivedRef.current) actions.disable()
          },

          onClick: (data, actions) => (waivedRef.current ? actions.resolve() : actions.reject()),

          createOrder: async () => {
            const { orderId } = await dispatch(createOrder({ product: product.id, language, waiveWithdrawal: waivedRef.current })).unwrap()
            return orderId
          },

          onApprove: async (data) => {
            // `data.orderID` is PayPal's, and our server only accepts one it created for this
            // player — an approved order id from somewhere else finds no row and is refused.
            const result = await dispatch(captureOrder({ orderId: data.orderID })).unwrap()
            if (!cancelled) handlers.current.onPaid?.(result)
          },

          onError: () => {
            if (!cancelled) handlers.current.onFailed?.()
          },
        })

        if (!buttons.isEligible()) {
          // No funding source this browser can use. Not an error — it is PayPal saying it cannot
          // serve this player here, and a blank space would leave them waiting for a button.
          setState("ineligible")
          return
        }

        buttons.render(container.current)
        setState("ready")
      })
      .catch(() => {
        if (!cancelled) setState("unavailable")
      })

    return () => {
      cancelled = true
      // PayPal keeps a live iframe per button set. Without this, opening the shop a second time
      // leaves the first set attached to a node React has already thrown away.
      try {
        buttons?.close()
      } catch (error) {
        // `close()` throws if the buttons never rendered (an unmount during load). Nothing to
        // clean up in that case, and an exception here would take the unmount down with it.
      }
    }
  }, [clientId, currency, product, dispatch, language])

  if (!clientId) return null

  return (
    <div className="paypal-checkout">
      {state === "loading" ? <p className="p-mini">{t("shop.paypalLoading")}</p> : null}
      {state === "unavailable" ? <p className="p-mini shop-warning">{t("shop.paypalUnavailable")}</p> : null}
      {state === "ineligible" ? <p className="p-mini shop-warning">{t("shop.paypalIneligible")}</p> : null}
      <label className="paypal-waiver">
        <input type="checkbox" checked={waived} onChange={(event) => setWaived(event.target.checked)} />
        <span>{t("shop.waiver")}</span>
      </label>
      <a className="p-mini paypal-waiver-link" href={localizedPath("/terms", language)} target="_blank" rel="noreferrer">
        {t("shop.waiverTerms")}
      </a>
      {!waived ? <p className="p-mini shop-warning">{t("shop.waiverRequired")}</p> : null}
      <div ref={container} className={`paypal-checkout-target${waived ? "" : " is-disabled"}`} />
    </div>
  )
}

export default PayPalCheckout
